/**
 * `nextNewNoteTitle` — compute the next sequential "New note NN" title.
 *
 * Note creation is modal-less (UX fix): a new note is created immediately with an
 * auto-generated name and the user renames it inline afterwards. To avoid collisions and
 * give a predictable sequence, the name is the lowest unused `New note NN` (zero-padded to
 * two digits, starting at `01`). Existing titles that already follow the pattern are scanned
 * so a fresh note never reuses a number.
 */

const NEW_NOTE_RE = /^new note (\d+)$/i;

/**
 * Given the set of existing note titles, return the next `New note NN` name.
 *
 * - Scans for the highest `NN` already used by a `New note NN` title and returns the next.
 * - Pads to at least two digits (`01`, `02`, …, `10`, …, `100`).
 * - An empty corpus (or no matching titles) yields `New note 01`.
 */
export function nextNewNoteTitle(existingTitles: readonly string[]): string {
  let max = 0;
  for (const raw of existingTitles) {
    const match = NEW_NOTE_RE.exec(raw.trim());
    if (match !== null) {
      const n = Number.parseInt(match[1], 10);
      if (Number.isFinite(n) && n > max) max = n;
    }
  }
  return `New note ${String(max + 1).padStart(2, '0')}`;
}
